/**
 * The flow map editor's save machine, as a pure reducer.
 *
 * A map in the CRM saves itself: every finished gesture on the canvas is an
 * `edit`, and `useFlowMapDoc` sends the newest document shortly after the last
 * one. Three rules keep that safe. They live here, React-free, so they can be
 * tested without a browser or a server:
 *
 * 1. One request at a time. Edits made while a save is out are counted, and when
 *    its answer arrives the machine reads "dirty" instead of "saved", so the
 *    newest document follows in the next request. Two saves never race, which is
 *    what would let an older document land last.
 * 2. A conflict stops the autosave. When somebody else saved first, the server
 *    answers 409 with its own copy, and the machine holds that copy until the
 *    user picks: take theirs (`load-latest`) or overwrite it with this one
 *    (`keep-mine`, which rebases onto their version and saves again).
 * 3. A failed save waits. It is not retried in a loop; the next edit or the
 *    retry button starts the next attempt.
 *
 * Edits are counted, not stored: the hook holds the document itself. `rev`
 * counts this session's edits, `savedRev` says how many of them the server
 * holds, and `sendingRev` is the one in flight.
 */
import type { FlowMapView } from "../../../../lib/flowMap";

/** How long the map waits after the last edit before it saves. */
export const AUTOSAVE_DELAY_MS = 1200;

export type SaveStatusKind = "idle" | "dirty" | "saving" | "saved" | "conflict" | "error";

export interface SaveState {
  /** idle = loaded and untouched; saved = this session's edits are all on the server. */
  status: SaveStatusKind;
  /** The version the server holds for this map: the next save's `baseVersion`. */
  baseVersion: number;
  /** Edits made in this session. */
  rev: number;
  /** The newest edit the server holds. Below `rev` means unsaved work. */
  savedRev: number;
  /** The edit the request in flight carries, or null when nothing is out. */
  sendingRev: number | null;
  /** The server's map after a 409, until the user picks; null otherwise. */
  conflict: FlowMapView | null;
}

export type SaveEvent =
  | { type: "loaded"; version: number }
  | { type: "edit" }
  | { type: "save-start" }
  | { type: "save-ok"; version: number }
  | { type: "save-conflict"; current: FlowMapView }
  | { type: "save-error" }
  | { type: "load-latest" }
  | { type: "keep-mine" };

export function initialSaveState(version = 0): SaveState {
  return { status: "idle", baseVersion: version, rev: 0, savedRev: 0, sendingRev: null, conflict: null };
}

/** Work the server does not hold yet: queued, in flight, failed or held back by a conflict. */
export function hasUnsaved(s: SaveState): boolean {
  return s.rev > s.savedRev;
}

/** Edits no request has carried yet (neither saved nor in flight). */
export function hasQueuedEdits(s: SaveState): boolean {
  return s.rev > (s.sendingRev ?? s.savedRev);
}

/** A save may go out now: something to send, nothing in flight, no conflict waiting for the user. */
export function canStartSave(s: SaveState): boolean {
  return s.sendingRev === null && s.status !== "conflict" && s.rev > s.savedRev;
}

/**
 * The debounce should run: there are queued edits, and neither a conflict nor a
 * failed save is holding the autosave back. True while a request is out too,
 * so the delay after the last edit is kept even then; the save itself still
 * waits for `canStartSave`.
 */
export function wantsAutosave(s: SaveState): boolean {
  return (s.status === "dirty" || s.status === "saving") && hasQueuedEdits(s);
}

/**
 * The next state. An event that does not fit the current state (an answer with
 * no request out, a choice with no conflict) returns the SAME object, so a
 * caller can tell with `===` that nothing happened.
 */
export function reduceSave(s: SaveState, ev: SaveEvent): SaveState {
  switch (ev.type) {
    case "loaded":
      return initialSaveState(ev.version);
    case "edit": {
      // A conflict keeps its banner and a request in flight keeps its spinner;
      // the edit is counted either way and goes out with the next save.
      const status = s.status === "conflict" || s.status === "saving" ? s.status : "dirty";
      return { ...s, status, rev: s.rev + 1 };
    }
    case "save-start":
      if (!canStartSave(s)) return s;
      return { ...s, status: "saving", sendingRev: s.rev };
    case "save-ok": {
      if (s.sendingRev === null) return s;
      // The server may answer a save identical to what it holds with its
      // unchanged version. That is still a success: the version it names is the
      // right base for the next save either way.
      const savedRev = Math.max(s.savedRev, s.sendingRev);
      return { ...s, status: savedRev >= s.rev ? "saved" : "dirty", baseVersion: ev.version, savedRev, sendingRev: null };
    }
    case "save-conflict":
      if (s.sendingRev === null) return s;
      return { ...s, status: "conflict", sendingRev: null, conflict: ev.current };
    case "save-error":
      if (s.sendingRev === null) return s;
      return { ...s, status: "error", sendingRev: null };
    case "load-latest":
      // The hook swaps the local document for `conflict.doc`, so everything this
      // session did is now either on the server or deliberately dropped.
      if (!s.conflict) return s;
      return { ...s, status: "idle", baseVersion: s.conflict.version, savedRev: s.rev, conflict: null };
    case "keep-mine":
      // Rebase onto the version that beat us, then save over it.
      if (!s.conflict) return s;
      return { ...s, status: "dirty", baseVersion: s.conflict.version, conflict: null };
  }
}

function isFlowMapView(v: unknown): v is FlowMapView {
  if (!v || typeof v !== "object") return false;
  const r = v as { version?: unknown; doc?: unknown };
  if (typeof r.version !== "number" || !Number.isInteger(r.version) || r.version < 0) return false;
  const doc = r.doc as { nodes?: unknown; edges?: unknown } | null | undefined;
  return !!doc && typeof doc === "object" && Array.isArray(doc.nodes) && Array.isArray(doc.edges);
}

/**
 * What a failed save means for the machine: a 409 `flow_conflict` that carries
 * the server's map, or any other failure. Read from the error's shape (`status`
 * and `body`, which is what `ApiError` carries) rather than through
 * `instanceof`, so this file stays free of the fetch client. A conflict whose
 * body cannot be read is an error, not a conflict: without the server's map
 * there is nothing to offer the user.
 */
export function saveFailureEvent(err: unknown): SaveEvent {
  if (err && typeof err === "object") {
    const e = err as { status?: unknown; body?: unknown };
    if (e.status === 409 && e.body && typeof e.body === "object") {
      const body = e.body as { error?: unknown; current?: unknown };
      if (body.error === "flow_conflict" && isFlowMapView(body.current)) {
        return { type: "save-conflict", current: body.current };
      }
    }
  }
  return { type: "save-error" };
}
